// UnitConsts family + the InitClass parse chain — every gob class's static data, parsed
// from the SHIPPING GobTemplates.ini with the exact C++ transforms.
// game/ht.h:6430-6530 (structs), game/Unit.cpp:65-200 (UnitGob::InitClass),
// game/MobileUnit.cpp (MobileUnitGob::InitClass), game/Struct.cpp (StructGob::InitClass),
// game/Builder.cpp (BuilderGob::InitClass).
//
// Verified transforms: um = 1 << ut; TimeToBuild seconds -> UpdFromSec (csec*100/8);
// UIBounds x kwcTile16th(16); SortOffset x 16; FiringRate ms/10 ticks;
// ArmorStrength -> itofx (read by the SUBCLASS InitClass, not the base);
// every *MP value falls back to its SP twin; PowerSupply/Demand/UpgradeCost default 0;
// ReserveDimensions default to TileDimensions. The umCanBuild masks are class consts set
// by the per-class shims (HRC/VTS/HQ) — they land with those ports.

import type { SimContext } from '../context.ts';
import { IniReader } from '../data/ini.ts';
import { itofx } from '../math/fix.ts';
import { s16, idiv } from '../math/int32.ts';

// game/ht.h:761-763
export const kwcTile = 0x0100;
export const kwcTileHalf = kwcTile / 2;
export const kwcTile16th = kwcTile / 16;

// game/ht.h:1021-1030 — (csec * 100) / (kcmsUpdate / 10), integer division
export const UpdFromSec = (csec: number): number => idiv(csec * 100, 8);
// game/ht.h:1032-1034
export const UpdFromMsec = (cms: number): number => idiv(cms, 80);

// game/ht.h:6436-6441
export const kfUntcNotifyEnemyNearby = 0x0001;
export const kfUntcNotifyPowerLowHigh = 0x0002;
export const kfUntcLargeDefog = 0x0004;
export const kfUntcMobileUnitBuilder = 0x0008;
export const kfUntcStructureBuilder = 0x0010;
export const kfUntcHasFullnessIndicator = 0x0020;

export interface WRectLike {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

// game/ht.h:6430-6432
export class GobConsts {
  gt = 0;
}

// game/ht.h:6443-6494 — the Get* accessors switch on the GAME's multiplayer flag
export class UnitConsts extends GobConsts {
  protected ctx!: SimContext;
  ut = -1;
  um = 0;
  umPrerequisites = 0;
  upgmPrerequisites = 0;
  sfxImpact = 0;
  nCost = 0;
  nCostMP = 0;
  fxArmorStrength = 0;
  fxArmorStrengthMP = 0;
  nInfantryDamage = 0;
  nInfantryDamageMP = 0;
  nVehicleDamage = 0;
  nVehicleDamageMP = 0;
  nStructureDamage = 0;
  nStructureDamageMP = 0;
  ctFiringRate = 0;
  tcFiringRange = 0;
  cupdTimeToBuild = 0;
  cupdTimeToBuildMP = 0;
  szAniName = '';
  wrcUIBounds: WRectLike = { left: 0, top: 0, right: 0, bottom: 0 };
  wdySortOffset = 0;
  szName = '';
  szLongName = '';
  wf = 0;
  szDescription: string | null = null;

  bind(ctx: SimContext): this {
    this.ctx = ctx;
    return this;
  }

  GetCost(): number {
    return this.ctx.fMultiplayer ? this.nCostMP : this.nCost;
  }
  GetArmorStrength(): number {
    return this.ctx.fMultiplayer ? this.fxArmorStrengthMP : this.fxArmorStrength;
  }
  GetTimeToBuild(): number {
    return this.ctx.fMultiplayer ? this.cupdTimeToBuildMP : this.cupdTimeToBuild;
  }
  GetInfantryDamage(): number {
    return this.ctx.fMultiplayer ? this.nInfantryDamageMP : this.nInfantryDamage;
  }
  GetVehicleDamage(): number {
    return this.ctx.fMultiplayer ? this.nVehicleDamageMP : this.nVehicleDamage;
  }
  GetStructureDamage(): number {
    return this.ctx.fMultiplayer ? this.nStructureDamageMP : this.nStructureDamage;
  }
}

// game/ht.h:6496-6509
export class StructConsts extends UnitConsts {
  ctxTiles = 0; // ctx/cty renamed: the context field shadows
  ctyTiles = 0;
  ctxReserve = 0;
  ctyReserve = 0;
  nPowerDemand = 0;
  nPowerSupply = 0;
  nUpgradeCost = 0;
  sfxAbortRepair = 0;
  sfxRepair = 0;
  sfxDamaged = 0;
  sfxDestroyed = 0;
  sfxSelect = 0;
  sfxUpgrade = 0;
  sfxAbortUpgrade = 0;
  // ht.h:6513 sfxUnitReady — declared on BuilderConsts in C++; Replicator.cpp builds a
  // BuilderConsts and assigns it, but the TS Replicator stores a StructConsts. `declare`
  // makes it visible to the type system WITHOUT emitting a field initializer (type-only;
  // no runtime property added to other StructConsts instances).
  declare sfxUnitReady: number;
}

// game/ht.h:6511-6517
export class BuilderConsts extends StructConsts {
  nBuildRate = 0;
  sfxUnitBuild = 0;
  sfxUnitBuildAbort = 0;
  sfxUnitReady = 0;
  umCanBuild = 0;
}

// game/ht.h:6519-6523
export class TowerConsts extends StructConsts {
  sfxAttack = 0;
  sfxFire = 0;
  // ht.h:6520 int *anFiringStripIndices — TowerConsts has its OWN turret strip table
  // (separate from MobileUnitConsts'). InitTowerClass assigns it at runtime; `declare`
  // exposes the type without emitting an initializer (type-only).
  declare anFiringStripIndices: number[];
}

// game/ht.h:6525+
export class MobileUnitConsts extends UnitConsts {
  sfxFire = 0;
  sfxcDestroyed = 0;
  sfxcSelect = 0;
  sfxcMove = 0;
  sfxcAttack = 0;
  wcMoveDistPerUpdate = 0;
  wcMoveDistPerUpdateMP = 0;
  // ht.h:6531-6533 — per-Direction16 strip tables, filled by each subclass's InitClass
  anFiringStripIndices: number[] = [];
  anMovingStripIndices: number[] = [];
  anIdleStripIndices: number[] = [];

  GetMoveDistPerUpdate(): number {
    return this.ctx.fMultiplayer ? this.wcMoveDistPerUpdateMP : this.wcMoveDistPerUpdate;
  }
}

// ht.h:6541-6545 — the upgradeable mobile-unit builders (HRC/VTS); pfrmBuild is forms UI
export class MobileUnitBuilderConsts extends BuilderConsts {
  fUpgrade = 0;
  fUpgradeInProgress = 0;
}

// ht.h:6547-6549 — takeover-specialist sfx (host-bound)
export class SpConsts extends MobileUnitConsts {
  sfxStructureCaptured = 0;
}

// ht.h:6551-6554 — miner-specific sfx (host-bound)
export class MinerConsts extends MobileUnitConsts {
  sfxMine = 0;
  sfxUnderAttack = 0;
}

// game/Unit.cpp:65-200 — the base InitClass. The pak/render arms (LoadAnimationData,
// InitFingerUIBounds, scorch bitmaps) are host/data-boundary: szAniName is kept and the
// AnimData registry binds at level load.
export function InitUnitConstsClass(ctx: SimContext, puntc: UnitConsts, pini: IniReader): boolean {
  ctx.unitConsts[puntc.ut] = puntc;
  puntc.um = (1 << puntc.ut) >>> 0;

  const szTemplate = String(puntc.gt); // itoa(gt)

  puntc.nInfantryDamage = 0;
  puntc.nVehicleDamage = 0;
  puntc.nStructureDamage = 0;
  puntc.nInfantryDamageMP = 0;
  puntc.nVehicleDamageMP = 0;
  puntc.nStructureDamageMP = 0;
  puntc.ctFiringRate = 0;
  puntc.tcFiringRange = 0;

  // required
  const cost = pini.GetInt(szTemplate, 'Cost');
  if (cost === null) return false;
  puntc.nCost = cost;

  const csec = pini.GetInt(szTemplate, 'TimeToBuild');
  if (csec === null) return false;
  puntc.cupdTimeToBuild = UpdFromSec(csec);
  const csecMP = pini.GetInt(szTemplate, 'TimeToBuildMP');
  puntc.cupdTimeToBuildMP = csecMP === null ? puntc.cupdTimeToBuild : UpdFromSec(csecMP);

  const bounds: (keyof WRectLike)[] = ['left', 'top', 'right', 'bottom'];
  const names = ['UIBoundsLeft', 'UIBoundsTop', 'UIBoundsRight', 'UIBoundsBottom'];
  for (let i = 0; i < 4; i++) {
    const pc = pini.GetInt(szTemplate, names[i]);
    if (pc === null) return false;
    puntc.wrcUIBounds[bounds[i]] = s16(pc * kwcTile16th);
  }

  const sort = pini.GetInt(szTemplate, 'SortOffset');
  if (sort === null) return false;
  puntc.wdySortOffset = sort * 16;

  const ani = pini.GetPropertyValue(szTemplate, 'Animation', '%s');
  if (ani.count !== 1) return false;
  puntc.szAniName = ani.values[0] as string;

  const name = pini.GetPropertyValue(szTemplate, 'Name', '%s');
  if (name.count !== 1) return false;
  puntc.szName = name.values[0] as string;

  // optional
  const cmsFiringRate = pini.GetInt(szTemplate, 'FiringRate');
  if (cmsFiringRate !== null) puntc.ctFiringRate = idiv(cmsFiringRate, 10);

  const inf = pini.GetInt(szTemplate, 'InfantryDamage');
  if (inf !== null) puntc.nInfantryDamage = inf;
  const infMP = pini.GetInt(szTemplate, 'InfantryDamageMP');
  puntc.nInfantryDamageMP = infMP === null ? puntc.nInfantryDamage : infMP;

  const veh = pini.GetInt(szTemplate, 'VehicleDamage');
  if (veh !== null) puntc.nVehicleDamage = veh;
  const vehMP = pini.GetInt(szTemplate, 'VehicleDamageMP');
  puntc.nVehicleDamageMP = vehMP === null ? puntc.nVehicleDamage : vehMP;

  const str = pini.GetInt(szTemplate, 'StructureDamage');
  if (str !== null) puntc.nStructureDamage = str;
  const strMP = pini.GetInt(szTemplate, 'StructureDamageMP');
  puntc.nStructureDamageMP = strMP === null ? puntc.nStructureDamage : strMP;

  const range = pini.GetInt(szTemplate, 'FiringRange');
  if (range !== null) puntc.tcFiringRange = range;

  puntc.szDescription = pini.GetPropertyString(szTemplate, 'Description');

  const longName = pini.GetPropertyString(szTemplate, 'LongName');
  puntc.szLongName = longName === null ? puntc.szName : longName;

  const costMP = pini.GetInt(szTemplate, 'CostMP');
  puntc.nCostMP = costMP === null ? puntc.nCost : costMP;

  return true;
}

// game/MobileUnit.cpp MobileUnitGob::InitClass — MoveRate raw WCoord (< 64: the
// gawcDiagonalDist table bound), ArmorStrength -> itofx, MP fallbacks.
export function InitMobileUnitConstsClass(
  ctx: SimContext,
  pmuntc: MobileUnitConsts,
  pini: IniReader,
): boolean {
  if (!InitUnitConstsClass(ctx, pmuntc, pini)) return false;
  const szTemplate = String(pmuntc.gt);

  const moveRate = pini.GetInt(szTemplate, 'MoveRate');
  if (moveRate === null) return false;
  pmuntc.wcMoveDistPerUpdate = s16(moveRate); // Assert(< 64): gawcDiagonalDist has 64 entries
  ctx.gwcMoveDistPerUpdateMin = Math.min(ctx.gwcMoveDistPerUpdateMin, pmuntc.wcMoveDistPerUpdate);
  ctx.gwcMoveDistPerUpdateMax = Math.max(ctx.gwcMoveDistPerUpdateMax, pmuntc.wcMoveDistPerUpdate);

  const armor = pini.GetInt(szTemplate, 'ArmorStrength');
  if (armor === null) return false;
  pmuntc.fxArmorStrength = itofx(armor);
  ctx.gfxMobileUnitArmorStrengthMin = Math.min(
    ctx.gfxMobileUnitArmorStrengthMin,
    pmuntc.fxArmorStrength,
  );
  ctx.gfxMobileUnitArmorStrengthMax = Math.max(
    ctx.gfxMobileUnitArmorStrengthMax,
    pmuntc.fxArmorStrength,
  );

  const armorMP = pini.GetInt(szTemplate, 'ArmorStrengthMP');
  pmuntc.fxArmorStrengthMP = armorMP === null ? pmuntc.fxArmorStrength : itofx(armorMP);

  const moveRateMP = pini.GetInt(szTemplate, 'MoveRateMP');
  pmuntc.wcMoveDistPerUpdateMP = moveRateMP === null ? pmuntc.wcMoveDistPerUpdate : s16(moveRateMP);

  // LoadMenu / move-target + explosion AnimationData: host/data boundary
  return true;
}

// game/Struct.cpp StructGob::InitClass — TileDimensions "%d,%d", ArmorStrength -> itofx,
// ReserveDimensions default to tile dims, power/upgrade defaults 0.
export function InitStructConstsClass(
  ctx: SimContext,
  pstruc: StructConsts,
  pini: IniReader,
): boolean {
  if (!InitUnitConstsClass(ctx, pstruc, pini)) return false;
  const szTemplate = String(pstruc.gt);

  const dims = pini.GetPropertyValue(szTemplate, 'TileDimensions', '%d,%d');
  if (dims.count !== 2) return false;
  pstruc.ctxTiles = dims.values[0] as number;
  pstruc.ctyTiles = dims.values[1] as number;

  const armor = pini.GetInt(szTemplate, 'ArmorStrength');
  if (armor === null) return false;
  pstruc.fxArmorStrength = itofx(armor);

  const armorMP = pini.GetInt(szTemplate, 'ArmorStrengthMP');
  pstruc.fxArmorStrengthMP = armorMP === null ? pstruc.fxArmorStrength : itofx(armorMP);

  const reserve = pini.GetPropertyValue(szTemplate, 'ReserveDimensions', '%d,%d');
  if (reserve.count !== 2) {
    pstruc.ctxReserve = pstruc.ctxTiles;
    pstruc.ctyReserve = pstruc.ctyTiles;
  } else {
    pstruc.ctxReserve = reserve.values[0] as number;
    pstruc.ctyReserve = reserve.values[1] as number;
  }

  pstruc.nPowerSupply = pini.GetInt(szTemplate, 'PowerSupply') ?? 0;
  pstruc.nPowerDemand = pini.GetInt(szTemplate, 'PowerDemand') ?? 0;
  pstruc.nUpgradeCost = pini.GetInt(szTemplate, 'UpgradeCost') ?? 0;

  // (the gfxStructureArmor/gnPower min-max fleet stats feed UI spec bars only — skipped)
  // LoadMenu/LoadConfirmation/smoke+explosion anims: host/data boundary

  // Struct.cpp:130 — all structures defog a larger area (read by fog reveal + SetPosition)
  pstruc.wf |= kfUntcLargeDefog;
  return true;
}

// game/Builder.cpp BuilderGob::InitClass — + required BuildRate
export function InitBuilderConstsClass(
  ctx: SimContext,
  pbldrc: BuilderConsts,
  pini: IniReader,
): boolean {
  if (!InitStructConstsClass(ctx, pbldrc, pini)) return false;
  const rate = pini.GetInt(String(pbldrc.gt), 'BuildRate');
  if (rate === null) return false;
  pbldrc.nBuildRate = rate;
  return true;
}

// game/Unit.cpp:30-39 — gob type -> consts scan (savegames + INI spawns look up by gt)
export function GetUnitConsts(
  ctx: import('../context.ts').SimContext,
  gt: number,
): UnitConsts | null {
  for (let i = 0; i < ctx.unitConsts.length; i++) {
    const c = ctx.unitConsts[i] as UnitConsts | undefined;
    if (c !== undefined && c.gt === gt) return c;
  }
  return null;
}
